import axios from 'axios';
import { CONFIG_LOCAL_STORAGE, PATHS, FEATURE_FLAGS } from './../constants/Define';
import { getPathWithParams } from './../helpers/functions';
import Config from '../constants/Config';
import { LocalStorage } from './../utils';
import ApiResponse from './../models/ApiResponse';
import Methods from './Methods';
import history from './../helpers/history';
import Urls from './Urls';
import queryString from 'query-string';
import { getContentLanguage } from '../utils/commonUtils';

const clearAuthStorage = () => {
    [
        CONFIG_LOCAL_STORAGE.AUTHENTICATING,
        CONFIG_LOCAL_STORAGE.ACCESS_TOKEN,
        CONFIG_LOCAL_STORAGE.REFRESH_TOKEN,
        CONFIG_LOCAL_STORAGE.EXPIRES,
        CONFIG_LOCAL_STORAGE.PROFILE,
    ].forEach(LocalStorage.remove);
};

export default class ApiService {

    /**
     * Create instance service for every API service.
     * @param {Object} options - The options to create.
     * @param {string} options.baseURL - setting the host of the request, required.
     * @param {Object} [options.params] - url params of the request instance (ex: {pageIndex: 1, pageSize: 10}).
     * @param {string} options.endpoint - endpoint  to call the request to (ex: 'api/admin/login').
     * @param {Object} [options.headers] - headers of the request instance (ex: {'Content-Type' : 'application/json'}).
     * @param {Object} [options.endpointParams] - headers of the request instance (ex: {id: 'some-identity-id-string'}).
     * @param {function} [options.parser] - function to parse response body to whatever you what (ex: function(body) { return JSON.parse(body) } ).
     * @param {function} [options.dummy] - function to return a dumy data when api service is not ready yet (ex: function() { return 'some-fake-body-data' } ).
     * @param {boolean} [options.isDownload=false] - setting the request is a downloading request or not, default "false".
     * @param {boolean} [options.withoutAuth=false] - setting the request to skip the "Authorization" header, default "false".
     */
    constructor(options = {}) {
        this.initServiceInstance(options);
        this.endpointParams = options.endpointParams || {};
        this.endpoint = getPathWithParams(options.endpoint || undefined, this.endpointParams);
        this.params = options.params || {};
        this.headers = this.getRequestHeaders(options.headers || {});
        this.parser = options.parser || undefined;
        this.responseType = options.isDownload && 'blob';
        this.dummy = options.dummy || undefined;
    }

    initServiceInstance(options = {}) {
        if (!options.baseURL) {
            throw new Error('"baseUrl" is a required option for "ApiService" model')
        }
        const accessToken = LocalStorage.get(CONFIG_LOCAL_STORAGE.ACCESS_TOKEN);
        this.instance = axios.create({
            baseURL: options.baseURL,
            paramsSerializer: this.getRequestParams,
            timeout: options.timeout ?? 30000,
        });
        if (!options.withoutAuth && accessToken) {
            this.instance.defaults.headers.common['Authorization'] = { toString() { return `Bearer ${accessToken}` } };
        } else {
            if(this.instance.defaults?.headers?.common && this.instance.defaults.headers.common['Authorization']) {
                delete this.instance.defaults.headers.common['Authorization'];
            }
        }
    }

    getRequestData(data) {
        if (data && typeof data.export === 'function') {
            return data.export()
        }
        return data;
    }

    getRequestParams(params = {}) {
        return queryString.stringify(params)
    }

    getRequestHeaders(headers={}){
        const result = {};
        Object.keys(headers).forEach(header => {
            let key = header.toLowerCase();
            result[key] = headers[header];
        });
        result['accept-language'] = FEATURE_FLAGS.TRANSLATE_ENABLE ? (result['accept-language'] || getContentLanguage()) : undefined;
        return result;
    }

    request(method, reqData) {
        const data = this.getRequestData(reqData);
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        return new Promise((resolve, reject) => {
            if (this.dummy) {
                const result = this.dummy();
                resolve(result);
                return result;
            } else {
                // If data is FormData, don't set Content-Type to let browser set it with boundary
                const isFormData = data instanceof FormData;
                const config = {
                    params: this.params,
                    headers: {
                        'Accept': 'application/json',
                        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                        ...this.headers
                    },
                    data: method === Methods.POST || method === Methods.PUT ? undefined : data,
                    responseType: this.responseType,
                };
                const secondParam = method === Methods.POST || method === Methods.PUT ? data : config;
                const thirdParam = method === Methods.POST || method === Methods.PUT ? config : undefined;
                const isAuthRequest = this.endpoint === Urls.LOGIN || this.endpoint === Urls.REFRESH;
                this.instance[method](this.endpoint, secondParam, thirdParam).then(response => {
                    if (response && response.data && response.data.errors && response.data.errors.length) {
                        const message = typeof response.data.errors[0] === 'string' ? response.data.errors[0] : response.data.errors.title || response.message;
                        const errorResp = new ApiResponse({ ...response, message, errors: response.data.errors, request: reqData, success: true });
                        reject(errorResp)
                        return errorResp;
                    }
                    const result = this.parser ? this.parser(response.data) : new ApiResponse({ ...response, request: reqData, success: true })
                    resolve(result);
                    return result;
                }).catch(err => {
                    if (err.response && err.response.status === 401 && !isAuthRequest) {
                        clearAuthStorage();
                        window.location.replace(PATHS.LOGIN.path);
                    } else if (err.response && err.response.status === 403 && !isAuthRequest) {
                        history.push(PATHS.PAGE_403.path)
                        history.go();
                    } else {
                        const errorRes = new ApiResponse({ ...err.response, ...(err.response || {}).data, request: reqData, success: false })
                        reject(errorRes)
                        return errorRes;
                    }

                })
            }

        });
    }

    /**
     * Make a GET request to the api service.
     * @param {any} body - setting the body of the request.
     */
    get = (body) => this.request(Methods.GET, body);

    /**
     * Make a POST request to the api service.
     * @param {any} data - setting the body data of the request.
     */
    post = (data) => this.request(Methods.POST, data);

    /**
     * Make a POST request to the api service.
     * @param {any} data - setting the body data of the request.
     */
    put = (data) => this.request(Methods.PUT, data);

    delete = (data) => this.request(Methods.DELETE, data);
}
